import {
  CURRENCY_OPTIONS,
  OPERATIONS,
  OPERATION_LABEL,
  formatListingPrice,
} from './listingCoreConstants';

export const LOT_CONTENT_MAX_TITLE = 100;
export const LOT_CONTENT_MAX_DESC = 500;
export const LOT_CONTENT_MAX_OBS = 300;

export const LOT_CONTENT_CURRENCIES = CURRENCY_OPTIONS;
export const LOT_CONTENT_OPERATIONS = OPERATIONS;
export const LOT_CONTENT_OPERATION_LABEL = OPERATION_LABEL;

export const emptyLotUserContentForm = () => ({
  unit_label: '',
  title: '',
  description: '',
  observations: '',
  price: '',
  currency: 'USD',
  operation: '',
  list_ids: [],
});

/**
 * El contenido del lote se comparte siempre que tenga datos (como UF y CAD).
 * Se mandan los flags en true por compatibilidad con la API.
 */
export const LOT_CONTENT_SHARE_FLAGS = Object.freeze({
  include_text_in_share: true,
  include_commercial_in_share: true,
  include_images_in_share: true,
});

/** Normaliza GET /getlotusercontent → estado del formulario. */
export const mapLotUserContentFromApi = (data = {}) => {
  const listIdsFromLists = Array.isArray(data.lists)
    ? data.lists.map((row) => row?.id).filter(Boolean)
    : [];
  const listIds = Array.isArray(data.list_ids) && data.list_ids.length
    ? data.list_ids.filter(Boolean)
    : listIdsFromLists;

  return {
    unit_label: data.unit_label || '',
    title: data.title || '',
    description: data.description ?? data.notes ?? '',
    observations: data.observations || '',
    price: data.price != null && data.price !== '' ? String(data.price) : '',
    currency: data.currency || 'USD',
    operation: data.operation || '',
    list_ids: listIds,
  };
};

/** Payload PUT /upsertlotusercontent desde el formulario. */
export const buildLotUserContentUpsertPayload = (lotId, form, { listingCoreOnly = false } = {}) => {
  const priceRaw = String(form.price ?? '').trim();
  const parsedPrice = priceRaw === '' ? null : Number(priceRaw);
  const core = {
    lot_id: lotId,
    title: form.title?.trim() || null,
    description: form.description?.trim() || null,
    price: Number.isFinite(parsedPrice) ? parsedPrice : null,
    currency: form.currency || null,
    operation: form.operation || null,
  };
  if (listingCoreOnly) return { ...core, ...LOT_CONTENT_SHARE_FLAGS };

  return {
    ...core,
    unit_label: form.unit_label?.trim() || null,
    observations: form.observations?.trim() || null,
    price_type: null,
    list_ids: Array.isArray(form.list_ids) ? form.list_ids.filter(Boolean) : [],
    ...LOT_CONTENT_SHARE_FLAGS,
  };
};

export const formatLotUserContentPrice = (content) => (
  formatListingPrice(content?.price, content?.currency)
);

export const hasLotUserContentCommercialData = (content) => {
  const hasPrice = content?.price != null && content?.price !== '';
  const hasOperation = Boolean(String(content?.operation || '').trim());
  return hasPrice || hasOperation;
};

export const buildLotUserContentCommercialSummary = (content) => {
  if (!hasLotUserContentCommercialData(content)) return null;

  const parts = [];
  const priceLabel = formatLotUserContentPrice(content);
  if (priceLabel) parts.push(priceLabel);

  const operation = content?.operation;
  if (operation && LOT_CONTENT_OPERATION_LABEL[operation]) {
    parts.push(LOT_CONTENT_OPERATION_LABEL[operation]);
  }

  return parts.length ? parts.join(' · ') : null;
};

/** true si hay algo publicable para mostrar en vistas compartidas. */
export const hasSharedLotUserContentDisplayData = (content) => {
  if (!content) return false;
  const title = String(content.title || '').trim();
  const description = String(content.description ?? content.notes ?? '').trim();
  const images = Array.isArray(content.images) ? content.images : [];
  const videos = Array.isArray(content.videos) ? content.videos : [];
  const attachments = Array.isArray(content.attachments) ? content.attachments : [];
  const commercialSummary = buildLotUserContentCommercialSummary(content);
  const showCommercial = hasLotUserContentCommercialData(content) && Boolean(commercialSummary);
  return Boolean(
    title || description || showCommercial || images.length > 0 || videos.length > 0 || attachments.length > 0,
  );
};
